import { computed, reactive, ref, watch } from 'vue'
import { fetchMetricSeries, fetchMonitoringStatus } from '@/services/monitoringApi'
import { useSelectedArea } from '@/composables/useAreaSelection'
import { resolveFeatureGeometry } from '@/composables/useFeatureGeometry'

export const DAY = 86_400_000
export const RANGE_DAYS = { '1y': 365, '1m': 30 }

export const METRICS = [
  { key: 'swc', title: 'Soil water', unit: '%', dp: 1 },
  { key: 'lst', title: 'Surface temp', unit: '°C', dp: 1 },
  { key: 'ndvi', title: 'NDVI', unit: '', dp: 2 },
  { key: 'biomass', title: 'Crop biomass', unit: 'index', dp: 2 },
  { key: 'canopyCover', title: 'Canopy cover', unit: '%', dp: 0 },
  { key: 'canopyHeight', title: 'Canopy height', unit: 'm', dp: 1 },
  { key: 'carbonDensity', title: 'Carbon density', unit: 't/ha', dp: 0 },
]

export function useMonitoring(orgId, projectId) {
  const selectedArea = useSelectedArea(projectId)

  const series = reactive(
    Object.fromEntries(
      METRICS.map((m) => [
        m.key,
        { loading: true, points: [], available: [], resolution: null, features: {} },
      ])
    )
  )
  const monitored = ref([])
  const geoms = reactive({})
  const expanded = ref(null)
  const activeRes = reactive({})
  const range = ref('1y')

  // The metrics response carries every monitored feature's latest value, so
  // expanding a card needs no further requests.
  const featureValues = computed(() =>
    (expanded.value && series[expanded.value]?.features) || {}
  )

  // Response payloads are compared before assigning: a fresh array or point
  // list identity would re-trigger every downstream watcher each poll.
  const lastJson = {}

  const refreshMetric = async (metric, resolution) => {
    const s = series[metric]
    // Captured before the await: a response for a stale selection is dropped
    // (the selection watcher has already started a correctly-scoped fetch).
    const featureCode = selectedArea.value?.code
    const params = {}
    if (featureCode) params.features = featureCode
    if (resolution) params.resolution = resolution
    try {
      const data = await fetchMetricSeries(orgId.value, projectId.value, metric, params)
      if (featureCode !== selectedArea.value?.code) return
      const points = data.points || []
      const json = JSON.stringify(points)
      if (json !== lastJson[metric]) {
        lastJson[metric] = json
        s.points = points
      }
      s.available = data.available || []
      s.resolution = data.resolution || null
      s.features = data.features || {}
    } catch (e) {
      // stale data stays; next poll retries
    }
    s.loading = false
  }

  let lastStatusJson = ''
  const refreshStatus = async () => {
    try {
      const rows = (await fetchMonitoringStatus(orgId.value, projectId.value)) || []
      const json = JSON.stringify(rows)
      if (json === lastStatusJson) return
      lastStatusJson = json
      monitored.value = rows
    } catch (e) {
      // keep the last good status; next poll retries
    }
  }

  let refreshing = false
  const refreshAll = async () => {
    if (refreshing) return
    refreshing = true
    try {
      await Promise.all([
        refreshStatus(),
        ...METRICS.map((m) => refreshMetric(m.key, activeRes[m.key])),
      ])
    } finally {
      refreshing = false
    }
  }

  const setResolution = (metric, res) => {
    activeRes[metric] = res
    series[metric].loading = true
    refreshMetric(metric, res)
  }

  const monitoredFor = (metric) =>
    monitored.value.filter((r) => (r.variants || []).some((v) => v.metric === metric))

  // Every subscription variant behind the metric ended in a failed state —
  // "syncing" would be a lie, the card should say the data isn't coming.
  const failedFor = (metric) => {
    const variants = monitored.value.flatMap((r) =>
      (r.variants || []).filter((v) => v.metric === metric && v.kind === 'pv')
    )
    if (!variants.length) return false
    return variants.every(
      (v) => v.status && (v.status.startsWith('Failed') || v.status === 'Rejected')
    )
  }

  const ensureGeometries = async (rows) => {
    await Promise.all(
      rows.map(async (r) => {
        const code = r.feature_code
        if (!code || geoms[code]) return
        const geometry = await resolveFeatureGeometry(code)
        if (geometry) geoms[code] = geometry
      })
    )
  }

  watch(monitored, (rows) => ensureGeometries(rows))

  watch(
    () => selectedArea.value?.code,
    () => {
      for (const m of METRICS) series[m.key].loading = true
      Promise.all(METRICS.map((m) => refreshMetric(m.key, activeRes[m.key])))
    }
  )

  let pollTimer = null
  const startPolling = () => {
    stopPolling()
    pollTimer = setInterval(() => {
      refreshAll()
    }, 60_000)
  }
  const stopPolling = () => {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  return {
    series,
    monitored,
    geoms,
    expanded,
    activeRes,
    featureValues,
    range,
    refreshAll,
    setResolution,
    startPolling,
    stopPolling,
    monitoredFor,
    failedFor,
  }
}
